import { logger } from "@sentry/nextjs";

import type { TransferSpeed } from "./v2/fees";
import type { IrisDelayReason } from "./v2/iris";
import type { CCTPVersion } from "./version";

export type CctpDirection = "deposit" | "withdraw";

export interface CctpTransferTags {
  /** Missing means v1: cards stored before v2 never recorded a version. `null` means unknown. */
  cctpVersion?: CCTPVersion | null;
  direction: CctpDirection;
  speed?: TransferSpeed;
  delayReason?: IrisDelayReason | null;
}

const versionTag = (version: CCTPVersion | null | undefined) =>
  version === null ? "unknown" : String(version ?? 1);

/**
 * A `Logger` context with the transfer's version, direction and speed as indexed Sentry tags, so
 * issues can be filtered v1 against v2 during the ramp. Every other field goes in `extra`: once a
 * context has `tags`, `Logger` forwards it as-is and would drop loose fields.
 */
export function cctpLogContext(transfer: CctpTransferTags, extra: Record<string, unknown> = {}) {
  return {
    tags: {
      cctpVersion: versionTag(transfer.cctpVersion),
      direction: transfer.direction,
      ...(transfer.speed && { speed: transfer.speed }),
      ...(transfer.delayReason && { delayReason: transfer.delayReason }),
    },
    extra,
  };
}

export type CctpUsageEvent = "claim" | "resume" | "status_poll" | "auto_import" | "fast_delayed";

const loggedOnceKeys = new Set<string>();

/**
 * Counts a CCTP event in Sentry Logs, which unlike `Logger.log` ships in production and opens no
 * issue. Stage B of the v2 cleanup waits for the v1 counts here to reach zero. With `onceKey`,
 * repeats of the same event and key are dropped until the page reloads.
 */
export function logCctpUsage(
  event: CctpUsageEvent,
  { chain, ...transfer }: CctpTransferTags & { chain?: string },
  { onceKey }: { onceKey?: string } = {},
): void {
  if (onceKey !== undefined) {
    const key = `${event}:${onceKey}`;
    if (loggedOnceKeys.has(key)) return;
    loggedOnceKeys.add(key);
  }
  try {
    logger.info(`cctp.${event}`, {
      event,
      ...cctpLogContext(transfer).tags,
      ...(chain && { chain }),
    });
  } catch {
    // Telemetry must never break a transfer.
  }
}
